"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Round = void 0;
const bitiotic_1 = require("@bitiotic/bitiotic");
const Persistent = require("./Persistent");
// TODO
//
// - cache the "guessLayout" value after its computed, and re-load it from saved Rounds
//   See resolveGuessesForAnswer.
//
/**
 * Proxy between a Persistent.Round and a running client's execution.  Tracks (remote) updates
 * to the round, and caches some values locally.
 *
 * Can create a new round or can load an existing round out of Firestore.
 */
class Round {
    static getPlayerInputCollection(ctx, roundDoc) {
        return roundDoc.makeCollectionRef(ctx, 'playerInput');
    }
    static getPlayerBetCollection(ctx, roundDoc) {
        return roundDoc.makeCollectionRef(ctx, 'playerBet');
    }
    static getPlayerBankCollection(ctx, roundDoc) {
        return roundDoc.makeCollectionRef(ctx, 'playerBank');
    }
    static getRoundLayoutDocRef(ctx, roundDoc) {
        return roundDoc.makeCollectionRef(ctx, 'guessLayout').makeDocRef('singleton');
    }
    static getRoundResultsDocRef(ctx, roundDoc) {
        return roundDoc.makeCollectionRef(ctx, 'roundResults').makeDocRef('singleton');
    }
    // Should be invoked via Game.newRound() wrapper
    static game__startNew(ctx, gameOptions, playerPaths, roundNumber, roundDoc, playerDrivers, roundCacheUpdate) {
        // Security rules are too complex, should only be invoked via Cloud Function
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        const state = {
            players: [...playerPaths],
            roundNumber,
            betSpots: fillBetSpots(ctx.newCtx('fillBetSpots'), gameOptions.betSpotCount(ctx)),
            // filled in by layoutGuesses() after players have filled in PlayerInputs.
            // guessLayout: {},
            // PlayerInput instances are created with the Round, and are updated as players
            // play.  Any updates after the results are aggregated will be ignored.
            // sub-collection: playerInput: {},
            // PlayerBank instances are created with the Round, and are updated as players
            // win monies.
            // sub-collection: playerBank: {},
        };
        const playerInput = Round.getPlayerInputCollection(ctx, roundDoc);
        const playerBet = Round.getPlayerBetCollection(ctx, roundDoc);
        const batch = bitiotic_1.FirebaseV9.writeBatch(ctx, roundDoc.getFirestore());
        ctx.log('Initializing new Round', roundDoc.id(), 'for', playerPaths);
        batch.setDoc(ctx, roundDoc, state);
        playerPaths.forEach(playerPath => {
            const d = playerDrivers[playerPath];
            const inputDefault = {
                guess: '',
            };
            if (d !== null) {
                ctx.assertDefined(d, 'Expect', playerPath, 'in', playerDrivers);
                inputDefault.driver = d;
            }
            const pInput = playerInput.makeDocRef(playerPath);
            ctx.log(`Creating per-Round playerInput for @${pInput.id()}`, inputDefault);
            batch.setDoc(ctx, pInput, inputDefault);
            const betDefault = {
                readyToGuess: false,
                bets: {},
            };
            if (d !== null) {
                ctx.assertDefined(d, 'Expect', playerPath, 'in', playerDrivers);
                betDefault.driver = d;
            }
            const pBet = playerBet.makeDocRef(playerPath);
            ctx.log(`Creating per-Round playerBet for @${pBet.id()}`, betDefault);
            batch.setDoc(ctx, pBet, betDefault);
        });
        const rpath = roundDoc.id();
        const r = new Round(ctx, gameOptions, roundDoc, state);
        roundCacheUpdate(rpath, r);
        return batch.commit(ctx)
            .catch((err) => {
            roundCacheUpdate(rpath, null);
            throw ctx.chainError(err, 'Starting new round', rpath);
        })
            .then(() => Promise.resolve(r));
    }
    /**
     * From given set of rounds, return the 'latest' one (has the highest Round Number).
     */
    static latest(ctx, rounds) {
        return Object.values(rounds).reduce((best, current) => {
            const bestId = best.getRoundNumber();
            const curId = current.getRoundNumber();
            ctx.assertNotEqual(bestId, curId, 'rounds must have unique ids', best.getRoundPath(ctx), current.getRoundPath(ctx));
            return bestId > curId ? best : current;
        });
    }
    constructor(ctx, gameOptions, roundDoc, roundState, roundResults = null, guessLayout = null) {
        // Cached state.  Much of this state is immutable once set, so the cache is easy
        // to keep consistent.
        this.readyPlayers = null;
        this.playerGuessPresence = null;
        this.guessLayout = null;
        this.playerBetMap = null;
        this.roundResults = null;
        // For tracking a sent/in-flight answer submission
        this.sentAGuess = false;
        this.sentAnswer = null;
        this.sentAnswerPromise = null;
        // Relatively unique identifier.  Just used for logging.
        // Firestore-defined ID is the real unique ID.
        this.instanceId = 'ID' + Math.floor(Math.random() * 1000 * 1000 * 10);
        this.roundDoc = roundDoc;
        this.playerInputColl = Round.getPlayerInputCollection(ctx, roundDoc);
        this.playerBetColl = Round.getPlayerBetCollection(ctx, roundDoc);
        this.playerBankColl = Round.getPlayerBankCollection(ctx, roundDoc);
        this.playerPathsCache = [...roundState.players];
        this.betSpotCache = roundState.betSpots;
        this.betSpotCount = gameOptions.betSpotCount(ctx);
        this.collapseDuplicateAnswersCache = gameOptions.collapseDuplicateAnswers(ctx);
        this.betGrantCount = gameOptions.betGrantCount(ctx);
        this.bestAnswerPayout = gameOptions.bestAnswerPayout(ctx);
        this.betsPerRound = gameOptions.betsPerRound(ctx);
        this.betUnit = gameOptions.betUnit(ctx);
        this.roundNumber = roundState.roundNumber;
        this.roundResults = roundResults;
        this.guessLayout = guessLayout;
        ctx.log('Created Round', this.instanceId, 'for', this.roundNumber, '/', this.roundDoc.id());
    }
    /** Return paths to all the possible players for this round. */
    getCachedPlayerPaths(_ctx) {
        // TODO: in the first round, this cache may get set up too early,
        // and not include all the players?
        return this.playerPathsCache;
    }
    /** Is given player an active player on this round? */
    hasActivePlayer(_ctx, playerPath) {
        return this.playerPathsCache.includes(playerPath);
    }
    getNumPlayers(_ctx) {
        return this.playerPathsCache.length;
    }
    getRoundNumber() {
        return this.roundNumber;
    }
    /** Fetch PlayerInput (player's guess) from Firebase for given Player on this Round. */
    fetchPlayerInput(ctx, playerPath) {
        const docRef = this.playerInputColl.makeDocRef(playerPath);
        return docRef.get().then(doc => {
            if (doc && doc.docExists()) {
                const blob = doc.data();
                if (blob) {
                    return Promise.resolve(blob);
                }
                else {
                    ctx.log('PlayerInput for', playerPath, 'exists, but is empty?!');
                }
            }
            return null;
        });
    }
    /** Fetch PlayerBets (bets + "ready" flag) from Firebase for given Player on this Round. */
    fetchPlayerBets(ctx, playerPath) {
        const docRef = this.playerBetColl.makeDocRef(playerPath);
        return docRef.get().then(doc => {
            if (doc && doc.docExists()) {
                const blob = doc.data();
                if (blob) {
                    return Promise.resolve(blob);
                }
                ctx.log('PlayerBets for', playerPath, 'exists, but is empty?!');
            }
            return null;
        });
    }
    /* Fetch PlayerBank (winnings for Round) from Firebase for given Player on this Round. */
    fetchPlayerBank(ctx, playerPath) {
        const docRef = this.playerBankColl.makeDocRef(playerPath);
        return docRef.get().then(doc => {
            if (doc && doc.docExists()) {
                const blob = doc.data();
                if (blob) {
                    return Promise.resolve(blob);
                }
                else {
                    ctx.log('PlayerBank for', playerPath, 'exists, but is empty?!');
                }
            }
            return null;
        });
    }
    /** Write into Firebase that given player is ready (in PlayerBet section). */
    setPlayerIsReady(ctx, playerPath) {
        const pBetDocRef = this.playerBetColl.makeDocRef(playerPath);
        ctx.log(`Notifying that ${playerPath} is ready @${pBetDocRef.debugString()}`);
        return pBetDocRef.patchIn(ctx, {
            readyToGuess: true,
        });
    }
    /** Write Player guess into Firebase (in PlayerInput section) */
    setGuess(ctx, playerPath, guess) {
        const pInputDocRef = this.playerInputColl.makeDocRef(playerPath);
        ctx.log(`Update ${playerPath} guess to "${guess}" @${pInputDocRef.debugString()}`);
        const patch = {
            guess,
        };
        // Update fails if document doesn't exist.  ACLs restrict writes to only the owner.
        return pInputDocRef.patchIn(ctx, patch)
            .then(() => {
            this.sentAGuess = true;
        });
    }
    getNumericGuess(_ctx, rawGuess) {
        const n = Number(rawGuess);
        if (isNaN(n)) {
            return 0;
        }
        return n;
    }
    /**
     * Fetch player guesses from Firebase, sort and deduplicate them, assign guesses
     * to (subset of) bet spots, and write back into Firebase GuessLayout document under the Round.
     *
     * Only the Round Manager should execute this.
     *
     * @param ctx
     * @returns
     */
    layoutGuesses(ctx, game) {
        ctx.assertDefined(game, 'Expect game.fbFuncs to be defined');
        ctx.assertDefined(game.fbFuncs, 'Expect game.fbFuncs to be defined');
        // Security rules are insane if clients can write to GuessLayout, so use a Cloud Function
        // to have the manager write back the winners
        const layoutGameGuesses = game.fbFuncs.findCallable('layoutRoundGuesses');
        const args = {
            gamePath: game.getGamePath(),
            roundPath: this.getRoundPath(ctx),
        };
        return layoutGameGuesses(args)
            .catch(err => {
            throw ctx.chainError(err, `on game ${game.getGamePath()}`);
        })
            .then(result => {
            ctx.log('Layout on Round', this.getRoundPath(ctx), 'completed', result);
            return result;
        });
    }
    // Invoked from Game.serverLayoutGuesses()
    internalLayoutGuesses(ctx) {
        return this.collectGuesses(ctx).then(guesses => {
            const count = guesses.length;
            ctx.assertLE(count, this.betSpotCount); // XXX should be impossible
            // Even or odd number of guesses are handled slightly differently (the "middle" bet
            // spot is skipped with an even number of guesses).
            const even = (count % 2) === 0;
            const evenLenAdj = even ? -1 : 0;
            const evenSkipIdx = Math.floor(this.betSpotCount / 2);
            const spare = this.betSpotCount - count + evenLenAdj; // number of unused ("spare") bet spots
            // spare must be even (that is what evenLenAdj insures)
            if ((spare % 2) !== 0) {
                throw ctx.newError(`Spare ${spare} nonsense: ${count} ${even} ${evenLenAdj}`);
            }
            const headSkip = Math.floor(spare / 2); // "Spare" is split between before and after
            const layoutMap = {};
            let guessIdx = 0;
            const maxIdx = this.betSpotCount - headSkip;
            for (let i = headSkip; i < maxIdx; i++) {
                if (even && (i === evenSkipIdx)) {
                    ctx.log('Skipping middle', evenSkipIdx);
                }
                else {
                    ctx.assertLT(guessIdx, guesses.length, 'guessIndex must fit in list of guesses');
                    const g = guesses[guessIdx];
                    ctx.assertDefined(g, `No guess at [${guessIdx}].  maxIdx: ${maxIdx}, headSkip: ${headSkip},` +
                        ` i:${i}, guesses.length: ${guesses.length}`);
                    if (g) {
                        layoutMap[Persistent.makeBetSpotPath(i)] = g;
                    }
                    guessIdx++;
                }
            }
            const layout = {};
            Object.keys(layoutMap).forEach(betSpot => {
                var _a;
                const guesses = (_a = layoutMap[betSpot]) === null || _a === void 0 ? void 0 : _a.playerGuesses;
                if (guesses) { // this should always be true
                    layout[betSpot] = guesses;
                }
            });
            return Round.getRoundLayoutDocRef(ctx, this.roundDoc).set(ctx, layout);
        });
    }
    /** Collect, from Firebase, all player's guesses for this round. */
    collectPlayerGuesses(ctx) {
        const playerPaths = this.getCachedPlayerPaths(ctx);
        const guesses = [];
        const fetchInputs = playerPaths.map(playerPath => {
            return this.fetchPlayerInput(ctx, playerPath).then(plyrInput => {
                if (plyrInput !== null) {
                    const rawGuess = plyrInput.guess; // may be emtpy string, which maps to guessValue 0.
                    const guessValue = this.getNumericGuess(ctx, rawGuess);
                    const r = {
                        rawGuess,
                        guessValue,
                        playerPath,
                    };
                    guesses.push(r);
                }
            });
        });
        // One Promise for all of the guesses
        return Promise.all(fetchInputs).then(() => {
            return guesses;
        });
    }
    /** Fetch from Firebase all guesses, organized by PlayerPath-indexed map */
    readPlayerGuesses(ctx) {
        return this.collectPlayerGuesses(ctx).then(guesses => {
            return guesses.reduce((map, next) => {
                map[next.playerPath] = next;
                return map;
            }, {});
        });
    }
    /**
     * Collect (from Firebase) raw guesses from all Players, and then sort and de-duplicate
     * the guesses into an ordered list for betting.
     *
     * It is possible that there are no valid guesses yet.
     */
    collectGuesses(ctx) {
        // Collect player's guesses from the per-player writable areas
        const guessListProm = this.collectPlayerGuesses(ctx);
        const doCollapse = this.collapseDuplicateAnswersCache;
        return guessListProm.then(guessList => {
            /* Filter out unset guesses */
            guessList = guessList.filter(guess => (guess.rawGuess !== undefined && guess.rawGuess !== ''));
            if (guessList.length < 1) {
                return [];
            }
            guessList.sort((a, b) => a.guessValue - b.guessValue);
            // Collpase playerGuesses into a list of numeric guesses with lists of players.
            // This is for supporting the collapse-duplicates option, but just use the same
            // data structure in both cases.
            const firstGuess = guessList[0];
            ctx.assertDefined(firstGuess); // XXX why do I need this assert? (ts)
            const guesses = [newCollapsedGuess(firstGuess)];
            ctx.assertDefined(guesses[0]); // XXX why do I need this assert? (ts)
            let prevGuess = guesses[0];
            for (let i = 1; i < guessList.length; i++) {
                const g = guessList[i];
                ctx.assertDefined(g);
                const nextVal = g.guessValue;
                if (doCollapse && (nextVal === prevGuess.guessValue)) {
                    ctx.assertUndefined(prevGuess.playerGuesses[g.playerPath], 'Player should not have existing guess', g.playerPath);
                    prevGuess.playerGuesses[g.playerPath] = g;
                }
                else {
                    prevGuess = newCollapsedGuess(g);
                    guesses.push(prevGuess);
                }
            }
            return guesses;
        });
        function newCollapsedGuess(g) {
            return {
                guessValue: g.guessValue,
                playerGuesses: {
                    [g.playerPath]: g,
                },
            };
        }
    }
    /** Set all bets for given player on this Round.  Overwrites any previous bet settings. */
    setBet(ctx, playerPath, bets) {
        // The betspotPath may be bogus.  That's okay.  Checked when doing payouts.  playerPath
        // is validated by Security Rules.
        const pBetRef = this.playerBetColl.makeDocRef(playerPath);
        ctx.log(playerPath, 'bet', bets);
        // XXX ugh, just zero out all unset betspotPaths, so patches don't accumulate
        // if bets move from one path to another ...
        const betPatch = {};
        this.getBetSpotPaths(ctx).forEach(path => {
            if (bets[path]) {
                betPatch[path] = bets[path];
            }
            else {
                betPatch[path] = 0;
            }
        });
        const update = {
            bets: betPatch,
        };
        return pBetRef.patchIn(ctx, update);
    }
    /** Each spot may have more than one "identical" guess on it.  Pick one. */
    representativeGuess(g) {
        if (g) {
            const playerPaths = Object.keys(g);
            const player0 = playerPaths[0];
            if (player0) {
                return g[player0];
            }
        }
        return undefined;
    }
    getCachedGuessLayout(_ctx) {
        return this.guessLayout;
    }
    hasGuessLayout(_ctx) {
        return this.guessLayout !== null;
    }
    /** Get (potentially cached) layout of guesses on BetSpots */
    getGuessLayout(ctx) {
        if (this.guessLayout === null) {
            ctx.log('Round layout not cached yet, loading...');
            Round.getRoundLayoutDocRef(ctx, this.roundDoc).get().then(layoutDoc => {
                if (layoutDoc && layoutDoc.docExists()) {
                    const blob = layoutDoc.data();
                    ctx.assertDefined(blob);
                    this.guessLayout = blob;
                    return Promise.resolve(this.guessLayout);
                }
                return Promise.resolve(null);
            });
        }
        return Promise.resolve(this.guessLayout);
    }
    // Assign an answer, and compute the winners (guessing and betting) based on
    // that answer.
    //
    // Assumes only one manager will submit answer, so no race conditions, but want
    // to prevent a single manager from re-submitting.
    resolveGuessesForAnswer(ctx, answerStr, game) {
        const answer = this.getNumericGuess(ctx, answerStr);
        ctx.log('Answer is', answer);
        if (this.sentAnswer !== null) {
            if (this.sentAnswer === answer) {
                // idempotent if possible
                ctx.assertNotNull(this.sentAnswerPromise);
                return this.sentAnswerPromise;
            }
            return Promise.reject(ctx.newError('Cannot reset answer.  Was', this.sentAnswer, 'now', answer));
        }
        const sendProm = this.getGuessLayout(ctx).then(guessLayout => {
            // Search backward for first answer that isn't too large
            for (let i = this.betSpotCount - 1; i >= 0; i--) {
                const iPath = Persistent.makeBetSpotPath(i);
                const g = guessLayout === null || guessLayout === void 0 ? void 0 : guessLayout[iPath];
                if (g) {
                    const aGuess = this.representativeGuess(g);
                    if (aGuess && (aGuess.guessValue <= answer)) {
                        const ct = Object.keys(g).length;
                        const guessVal = aGuess.guessValue;
                        ctx.log(`${ct} guess(es) at ${i} (@${iPath}, ${guessVal}) win for ${answer}`);
                        Object.values(g)
                            .forEach(eachG => ctx.assertEqual(eachG.guessValue, guessVal, 'All guesses on spot', iPath, 'must have same value:', g));
                        return this.setRoundWinners(ctx, game, answer, iPath, g);
                    }
                }
            }
            // All guesses too high
            ctx.log('All guesses too high for', answer);
            return this.setRoundWinners(ctx, game, answer, Persistent.BetSpot_Underflow, null);
        });
        this.sentAnswerPromise = sendProm;
        this.sentAnswer = answer;
        return sendProm;
    }
    /**
     * Set winners, in Firebase, on this Round with given answer, spot and guess. To be executed by
     * Round's manager.
     *
     * Winnings from a round come from two sources:
     * - having the correct guess (more than one player can get this)
     * - having a winning bet (generally on the correct guess, but "top half"
     *   and "bottom half" bets might be supported)
     *
     * Because the betting API is open-ended, we "need" to enforce bet sanity, and do that here.
     * Losing bets are tallied first, in case too many bets were made.
     * - There is a limit on the amount of money that can be bet (all your previous winnings
     *   plus the grant)
     * - There is a limit on the number of bet spots that can be bet (usually 2).
     */
    setRoundWinners(ctx, game, answer, winningSpotPath, // TODO: drop this parameter, and compute it in here?
    guesses) {
        // Security rules are insane if clients can write to Round, so use a Cloud Function
        // to have the manager write back the winners
        const serverSetRoundWinners = game.fbFuncs.findCallable('setRoundWinners');
        const args = {
            gamePath: game.getGamePath(),
            roundPath: this.getRoundPath(ctx),
            winningSpotPath,
            answer,
            guesses,
        };
        return serverSetRoundWinners(args)
            .catch(err => {
            throw ctx.chainError(err, `on game ${game.getGamePath()}`);
        })
            .then(result => {
            ctx.log('Set winners on Round', this.getRoundPath(ctx), result);
        });
    }
    // Invoke via Cloud Function handler on Game
    internalSetRoundWinners(ctx, answer, winningSpotPath, guesses, budgets) {
        ctx.assertEqual(bitiotic_1.FirebaseV9.getFirebaseEnv(), 'ADMIN');
        const bankResults = {};
        // For accounting purposes, the grant is added to "winnings", and the cost of every bet
        // is subtracted from winnings, and then the "payout" from winning bets is added in.
        const roundGrant = this.betGrantCount * this.betUnit;
        function bankForPlayer(playerPath) {
            let b = bankResults[playerPath];
            if (!b) {
                b = bankResults[playerPath] = {
                    grant: roundGrant, // everyone starts with the grant
                    betcost: 0,
                    winnings: 0, // correct-guess payout + correct-bet payout(s)
                    net: 0,
                    // winningGuess: stays undefined
                    winningGuessPayout: 0,
                    betPayouts: {}, // filled in below
                };
            }
            return b;
        }
        if (guesses !== null) {
            // Add all the players with the same guess as winners and put guess winnings in
            // temporary per-player bank
            for (const [winnerPath, guess] of Object.entries(guesses)) {
                // XXX exact match bonus?
                const payout = this.bestAnswerPayout;
                ctx.log(`${winnerPath} best guess (${guess.guessValue} for ${answer})`, `got ${payout}`);
                const plyrBank = bankForPlayer(Persistent.asPlayerPath(winnerPath));
                plyrBank.winningGuess = guess.guessValue;
                plyrBank.winningGuessDelta = guess.guessValue - answer;
                plyrBank.winningGuessPayout = payout;
                plyrBank.winnings += payout;
            }
        }
        // XXX payout lowHalf/highHalf bets
        const buildBetLimits = budgets.then(budgets => {
            // Build betting limits from the budgets (prior winnings) blob
            const betLimits = {};
            this.getCachedPlayerPaths(ctx).forEach(playerPath => {
                const value = budgets[playerPath] || 0;
                betLimits[playerPath] = {
                    priorWinnings: value, // amount of winnings that can be bet, does not include per-round grant
                };
            });
            return betLimits;
        });
        // TODO: include top-half/bottom-half bets spots in the future.
        // TODO: And sort it by bet-spot multiplier (lower earlier)
        const betSpotPaths = this.getBetSpotPaths(ctx).sort();
        const winningBetPaths = [
            winningSpotPath,
        ];
        const buildBetMap = this.currentBetMap(ctx);
        return Promise.all([buildBetLimits, buildBetMap]).then(([limits, originalBetmap]) => {
            // For each player, accumulate their total bets, clipping anything out of bounds
            this.getCachedPlayerPaths(ctx).forEach(playerPath => {
                const plyrBank = bankForPlayer(playerPath);
                const playerBets = originalBetmap[playerPath];
                const betsPlaced = { count: 0, value: 0 };
                if (playerBets) {
                    // Loop through all the bad-bet spots first (so limits kick in on the
                    // winning bets, later)
                    for (const betSpotPath of betSpotPaths) {
                        if (!winningBetPaths.includes(betSpotPath)) {
                            const betValue = playerBets[betSpotPath];
                            if (betValue) {
                                ctx.log('Player', playerPath, 'resolve (losing) bets at', betSpotPath, 'with limits', limits[playerPath], 'and', betsPlaced);
                                // Doing the user a favor if we clipped a losing bet ...
                                const allowedBetValue = this.allowedBetValue(ctx, playerPath, betValue, limits, betsPlaced);
                                // TODO: Alert someone if bets are clipped?
                                if (allowedBetValue !== betValue) {
                                    ctx.log('Clipped', playerPath, '(losing) bet at', betSpotPath, `to $${allowedBetValue} from $${betValue}`, 'limits', limits[playerPath], 'placed', betsPlaced);
                                }
                                // Track the loss (maybe $0 after limits) in the bank
                                const betSpot = this.betSpotCache[betSpotPath];
                                ctx.assertDefined(betSpot); // XXX
                                ctx.log('Player', playerPath, `lost $${allowedBetValue} with a bet on`, betSpotPath);
                                // Add bet-based losses to player's per-Round bank
                                plyrBank.betcost += allowedBetValue;
                                ctx.assertUndefined(plyrBank.betPayouts[betSpotPath], 'Payout should not have', betSpotPath, 'yet:', plyrBank.betPayouts);
                                plyrBank.betPayouts[betSpotPath] = {
                                    multiplier: betSpot.payoutMultiplier, // not really needed on a losing spot ...
                                    bet: allowedBetValue,
                                    payout: 0,
                                    loss: allowedBetValue,
                                };
                            }
                        }
                    }
                    // Now do the winning bet spots (so limits will kick in correctly)
                    for (const betSpotPath of winningBetPaths) {
                        const betValue = playerBets[betSpotPath];
                        if (betValue) {
                            ctx.log('Player', playerPath, 'resolve (winning) bets at', betSpotPath, 'with limits', limits[playerPath], 'and', betsPlaced);
                            const allowedBetValue = this.allowedBetValue(ctx, playerPath, betValue, limits, betsPlaced);
                            // TODO: Alert someone if bets are clipped?
                            if (allowedBetValue !== betValue) {
                                ctx.log('Clipped', playerPath, '(winning) bet at', betSpotPath, `to $${allowedBetValue} from $${betValue}`);
                            }
                            const betSpot = this.betSpotCache[betSpotPath];
                            ctx.assertDefined(betSpot); // XXX handle?
                            const multiplier = betSpot.payoutMultiplier;
                            const winnings = allowedBetValue * multiplier;
                            ctx.log('Player', playerPath, `winning bet $${allowedBetValue}`, `(orig $${betValue}) at ${multiplier}x = $${winnings} at`, betSpotPath);
                            // Add bet-based winnings to player's per-Round bank, accounting for
                            // the cost of the bet too
                            plyrBank.betcost += allowedBetValue;
                            plyrBank.winnings += winnings;
                            ctx.assertUndefined(plyrBank.betPayouts[betSpotPath], 'Payout should not have', betSpotPath, 'yet:', plyrBank.betPayouts);
                            plyrBank.betPayouts[betSpotPath] = {
                                multiplier,
                                bet: allowedBetValue,
                                payout: winnings,
                                loss: 0,
                            };
                        }
                    }
                }
                plyrBank.net = plyrBank.winnings - plyrBank.betcost + plyrBank.grant;
                if (plyrBank.net > 0) {
                    ctx.log('Player', playerPath, `net +$${plyrBank.net} from $${plyrBank.winnings} - $${plyrBank.betcost} + $${plyrBank.grant}`);
                }
                else if (plyrBank.net === 0) {
                    ctx.log('Player', playerPath, `net $0 from $${plyrBank.winnings} - $${plyrBank.betcost} + $${plyrBank.grant}`);
                }
                else {
                    ctx.log('Player', playerPath, `net -$${0 - plyrBank.net} from $${plyrBank.winnings} - $${plyrBank.betcost} + $${plyrBank.grant}`);
                }
            });
        }).then(() => {
            // Batch write the results of the guesses and bets in one commit.  Add
            // the RoundResults and add a PlayerBank for each player that won
            // some money.
            const bankablePlayers = Object.keys(bankResults);
            const results = {
                answer,
                winningSpotPath,
                winningGuesses: guesses,
                winningGuessPayout: this.bestAnswerPayout,
                playerNet: bankablePlayers.reduce((obj, player) => {
                    var _a;
                    const net = (_a = bankResults[player]) === null || _a === void 0 ? void 0 : _a.net;
                    if (net) {
                        obj[player] = net;
                    }
                    return obj;
                }, {}),
            };
            const roundResultDocRef = Round.getRoundResultsDocRef(ctx, this.roundDoc);
            const batch = bitiotic_1.FirebaseV9.writeBatch(ctx, this.roundDoc.getFirestore());
            batch.setDoc(ctx, roundResultDocRef, results);
            bankablePlayers.forEach(playerPath => {
                const bank = bankResults[playerPath];
                if (bank) {
                    const bankDocRef = this.playerBankColl.makeDocRef(playerPath);
                    batch.setDoc(ctx, bankDocRef, bank);
                }
            });
            return batch.commit(ctx)
                .then(() => {
                // Update local cache of the (immutable) results
                this.roundResults = results;
            });
        });
    }
    // Bounds check the given user's given bet.  Check against accumulating betsPlaced.
    allowedBetValue(ctx, player, betvalue, limits, betsPlaced) {
        var _a, _b;
        const spotLimit = this.betsPerRound;
        const grantLimit = this.betGrantCount * this.betUnit;
        if (betsPlaced.count >= spotLimit) {
            ctx.log('TOO MANY BETS:', player, betsPlaced);
            return 0;
        }
        betsPlaced.count += 1;
        const priorWinnings = ((_b = (_a = limits[player]) === null || _a === void 0 ? void 0 : _a.priorWinnings) !== null && _b !== void 0 ? _b : 0);
        const maxSpend = grantLimit + priorWinnings;
        const newSpend = betsPlaced.value + betvalue;
        let allowedBet = betvalue;
        if (newSpend > maxSpend) {
            allowedBet = maxSpend - betsPlaced.value;
            ctx.assertGE(allowedBet, 0);
            ctx.assertLT(allowedBet, betvalue);
            ctx.log('Player', player, `bet $${betvalue}, total $${newSpend} exceeds $${maxSpend} ($${grantLimit} + $${priorWinnings}) => allowed bet: $${allowedBet}`);
        }
        betsPlaced.value += allowedBet;
        return allowedBet;
    }
    layoutDebugString(ctx) {
        return this.getGuessLayout(ctx).then(guessLayout => {
            return this.betsBySpotDebugString(ctx).then(betsBySpot => {
                const s = [];
                const betSpots = this.getBetSpotPaths(ctx).sort();
                betSpots.forEach(betSpotPath => {
                    const betStr = (betsBySpot[betSpotPath] || []).join(';');
                    const guessEntry = this.representativeGuess(guessLayout === null || guessLayout === void 0 ? void 0 : guessLayout[betSpotPath]);
                    if (guessEntry) {
                        const val = guessEntry.guessValue; // all same value by definition
                        s.push(`${betSpotPath}:["${val}" ${betStr}]`);
                    }
                    else {
                        s.push(`${betSpotPath}:[${betStr}]`);
                    }
                });
                return Promise.resolve(s.join(','));
            });
        });
    }
    // Returns map with all players, and true/false if player is
    // ready to guess.
    readyToGuessMap(ctx) {
        const readyPlayers = [];
        this.getCachedPlayerPaths(ctx).forEach(playerPath => {
            const p = this.fetchPlayerBets(ctx, playerPath).then(plyrBet => {
                let ready = false;
                if (plyrBet !== null) {
                    ready = !!plyrBet.readyToGuess;
                }
                return { [playerPath]: ready };
            });
            readyPlayers.push(p);
        });
        return Promise.all(readyPlayers).then(readies => {
            return Object.assign({}, ...readies);
        });
    }
    readyDebugString(ctx) {
        const strs = [];
        this.getCachedPlayerPaths(ctx).forEach(playerPath => {
            const p = this.fetchPlayerBets(ctx, playerPath).then(plyrBet => {
                if (plyrBet === null) {
                    return '';
                }
                else {
                    return `${playerPath}: ready=${plyrBet.readyToGuess}`;
                }
            });
            strs.push(p);
        });
        return Promise.all(strs).then(strings => {
            return strings.join(',');
        });
    }
    // Return map from betSpotPath to array of player+chip debug strings
    betsBySpotDebugString(ctx) {
        const bets = {};
        return this.forEachMadeBet(ctx, (playerPath, spotPath, chipPath) => {
            let p = bets[spotPath];
            if (!p) {
                p = bets[spotPath] = [];
            }
            // XXX validate chipPath
            p.push(`${playerPath}+${chipPath}`);
        }).then(() => {
            return Promise.resolve(bets);
        });
    }
    winningsDebugString(ctx) {
        const s = [];
        const promises = [];
        this.getCachedPlayerPaths(ctx).forEach(playerPath => {
            const p = this.fetchPlayerBank(ctx, playerPath).then(bank => {
                if (bank !== null) {
                    const playerStr = [];
                    if (bank.winningGuess !== undefined) {
                        playerStr.push('winning guess+' + this.bestAnswerPayout);
                    }
                    for (const [chipPath, payout] of Object.entries(bank.betPayouts)) {
                        playerStr.push(`${chipPath}:${payout.bet}x${payout.multiplier}=${payout.payout}`);
                    }
                    if (playerStr.length > 0) {
                        s.push(`${playerPath}[${playerStr.join(' ')}]`);
                    }
                }
            });
            promises.push(p);
        });
        return Promise.all(promises).then(() => {
            return s.join(';');
        });
    }
    // Returns promise to have invoked callback on all players+betspots
    forEachMadeBet(ctx, callback) {
        const promises = [];
        const spotPaths = this.getBetSpotPaths(ctx);
        this.getCachedPlayerPaths(ctx).forEach(playerPath => {
            const p = this.fetchPlayerBets(ctx, playerPath).then(plyrBet => {
                if (plyrBet !== null) {
                    const pBets = plyrBet.bets;
                    spotPaths.forEach(spotPath => {
                        const val = pBets[spotPath];
                        if (val) {
                            callback(playerPath, spotPath, val);
                        }
                    });
                }
                else {
                    ctx.log('No bets from', playerPath);
                }
            });
            promises.push(p);
        });
        return Promise.all(promises);
    }
    getRoundLabel(_ctx) {
        return this.roundDoc.id().slice(0, 6);
    }
    getRoundPath(_ctx) {
        return Persistent.asRoundPath(this.roundDoc.id());
    }
    /**
     * Return paths to all betting spots (including "underflow" and "lowHalf", etc).
     *
     * See fillBetSpots()
     */
    getBetSpotPaths(_ctx) {
        const spots = Object.keys(this.betSpotCache);
        return spots;
    }
    getBetSpot(ctx, betSpotPath) {
        const spot = this.betSpotCache[betSpotPath];
        ctx.assertDefined(spot);
        return spot;
    }
    /** Return paths to all the relevant betting spots (skip ones with no guesses). */
    getRelevantBetSpotPaths(ctx) {
        const allPaths = this.getBetSpotPaths(ctx);
        const layout = this.guessLayout;
        // If there is no layout yet, all paths are "relevant"
        if (layout === null) {
            return allPaths;
        }
        return allPaths.filter((path) => {
            // Keep always-legal paths
            if (path === Persistent.BetSpot_Underflow) {
                return true;
            }
            // ... and and that show up in the layout (i.e. have a guess)
            const guesses = layout[path];
            if (guesses) {
                ctx.assertGT(Object.keys(guesses).length, 0);
            }
            return !!guesses;
        });
    }
    getPlayerRoundWinnings(ctx, playerPath) {
        return this.fetchPlayerBank(ctx, playerPath).then(bank => {
            if (bank === null) {
                ctx.log('Player', playerPath, 'has no bank this round.');
                return null;
            }
            let totalBetcost = 0;
            let losingBetcost = 0;
            let losingBetCount = 0;
            let betWinnings = 0;
            for (const betpayout of Object.values(bank.betPayouts)) {
                if (betpayout.loss > 0) {
                    losingBetcost += betpayout.bet;
                    losingBetCount += 1;
                }
                if (betpayout.payout > 0) {
                    betWinnings += betpayout.payout;
                }
                totalBetcost += betpayout.bet;
            }
            const grantRemainder = bank.grant - totalBetcost;
            const excessBet = totalBetcost - bank.grant;
            let winningBetExcess = 0; // amount beyond grant spent on winning bet
            let losingBetExcess = 0; // amoutn beyonnd grant attributed to losing bet(s)
            if (excessBet > 0) {
                // Excess first goes to the losing bets (this is a UI decision not a
                // correctness decision)
                ctx.assertLE(losingBetcost, totalBetcost);
                let grant = bank.grant;
                winningBetExcess = totalBetcost - losingBetcost;
                ctx.log('1-WBE=', winningBetExcess, '=', totalBetcost, '-', losingBetcost);
                const winningCover = Math.min(winningBetExcess, grant);
                ctx.log(' winningCover =', `min(${winningBetExcess}, ${grant})`);
                winningBetExcess = winningBetExcess - winningCover;
                ctx.log('2-WBE=', winningBetExcess, '= (prev) -', winningCover);
                grant = grant - winningCover;
                losingBetExcess = losingBetcost - Math.min(losingBetcost, grant);
                ctx.log('LBE=', losingBetExcess, '=', losingBetcost, '-', `min(${losingBetcost}, ${grant})`);
                ctx.assertGE(winningBetExcess, 0);
                ctx.assertGE(losingBetExcess, 0);
                ctx.assertLE(winningBetExcess, totalBetcost);
                ctx.assertLE(losingBetExcess, totalBetcost);
                ctx.assertEqual(totalBetcost, bank.grant + losingBetExcess + winningBetExcess);
            }
            return {
                grant: bank.grant,
                losingBetcost,
                losingBetCount,
                totalBetcost,
                grantRemainder,
                excessBet,
                winningBetExcess,
                losingBetExcess,
                winnings: bank.winnings,
                net: bank.net,
                winningGuessPayout: bank.winningGuessPayout,
                winningGuess: bank.winningGuess,
                winningGuessDelta: bank.winningGuessDelta,
                betPayouts: { ...bank.betPayouts },
                betWinnings,
            };
        });
    }
    allPlayersReady(ctx) {
        if (this.readyPlayers === null) {
            return false; // as far as we know, its not ready yet
        }
        const playerCt = this.getNumPlayers(ctx);
        return this.readyPlayers.size === playerCt;
    }
    isPlayerReady(ctx, playerPath) {
        if (this.readyPlayers === null) {
            return false; // as far as we know, its not ready yet
        }
        return this.readyPlayers.has(playerPath);
    }
    allGuessesPresent(ctx) {
        if (this.playerGuessPresence === null) {
            return false; // as far as we know, guesses are not present
        }
        const playerCt = this.getNumPlayers(ctx);
        return this.playerGuessPresence.size === playerCt;
    }
    isGuessPresent(_ctx, playerPath) {
        if (this.playerGuessPresence === null) {
            return null;
        }
        return this.playerGuessPresence.has(playerPath);
    }
    /**
     * Compute the expected next action from the given user on this Round.
     *
     * @param ctx
     * @param playerPath
     * @returns PlayAction enum entry
     */
    getPlayerAction(ctx, playerPath) {
        let action = 'unknown';
        if (this.roundResults !== null) { // if results are present, round is over
            return 'close-round';
        }
        // If the layout is found, definitely in betting mode, otherwise not clear
        if (this.guessLayout !== null) {
            ctx.log('Guess layout is available, so probably betting', this.instanceId);
            action = 'bet-missing-chips'; // will only bet missing chips, so may be done effectively
        }
        else {
            ctx.log('No guess layout, so some guesses may be missing...');
            if (this.sentAGuess) {
                ctx.log('My guess is sent, so need to wait for layout before betting');
                action = 'wait-for-guess-layout';
            }
            else {
                ctx.log('sentAGuess is', this.sentAGuess, 'so checking readiness...', this.instanceId);
                // Technically this client could've missed their chance at guessing,
                // but hasn't seen the "layout" yet, so thinks they can still guess.
                // They'll be ignored.
                if (this.readyPlayers !== null) {
                    const amReady = this.readyPlayers.has(playerPath);
                    if (!amReady) {
                        ctx.log('I am not marked ready yet');
                        action = 'declare-ready';
                    }
                    else {
                        ctx.log('I am ready, so should guess');
                        action = 'guess';
                    }
                }
                else {
                    // No guess, but its also not clear if I've marked "ready" yet or not ...
                    // so 'unknown'?
                    ctx.log('readyPlayers is', this.readyPlayers, 'and sentAGuess is', this.sentAGuess, ' so unknown action?', this.instanceId);
                    action = 'unknown';
                }
            }
        }
        return action;
    }
    retryOpts(ctx) {
        const ctxConfig = ctx.getRetryConfig();
        return {
            backoffCfg: new bitiotic_1.Backoff.BackoffConfig(ctxConfig.timeoutMS, ctxConfig.maxTimeoutMS),
            maxTries: ctxConfig.maxTries,
            boundWait: Boolean(ctxConfig.boundWait),
            log: (s) => {
                if (ctxConfig.logEnabled) {
                    ctx.log(s);
                }
            }
        };
    }
    getReadyPlayersCache(_ctx) {
        return this.readyPlayers;
    }
    // Listen for one valid "player ready" update from the given player on this Round.
    listenForPlayerReady(ctx, playerPath, cb, errCb) {
        const unsub = bitiotic_1.CancelTracker.track(`${this.getRoundPath(ctx)}.playerBet.${playerPath}.readyToGuess`, this.playerBetColl
            .makeDocRef(playerPath)
            .onSnapshot(docSnap => {
            if (docSnap && docSnap.docExists()) {
                const state = docSnap.data();
                // State goes from false to true at most once
                if (state && state.readyToGuess) {
                    unsub();
                    // Update cache:
                    if (this.readyPlayers === null) {
                        this.readyPlayers = new Set();
                    }
                    this.readyPlayers.add(playerPath);
                    cb(ctx, state.readyToGuess);
                }
            }
        }, err => {
            errCb(ctx.chainError(err, `PlayerBet.${playerPath} onSnapshot for readyToGuess`));
        }));
        return unsub;
    }
    // Listen for players that are ready to guess.  Caller must invoke cancel callback
    // once they've seen all players.  Callback is invoked with batches of recently
    // updated players.
    listenForReadyPlayers(ctx, cb, errCb) {
        ctx = ctx.newCtx('listenForReadyPlayers');
        const unsub = bitiotic_1.Retry.retryable(retryCtl => {
            return bitiotic_1.CancelTracker.track(`${this.getRoundPath(ctx)}.playerBet.*.readyToGuess`, this.playerBetColl
                .onSnapshot(updates => {
                if (this.readyPlayers === null) {
                    this.readyPlayers = new Set();
                }
                const rp = this.readyPlayers;
                const playerPaths = [];
                updates.forEach(betRef => {
                    var _a;
                    if (((_a = betRef.data()) === null || _a === void 0 ? void 0 : _a.readyToGuess) === true) {
                        const ppath = Persistent.asPlayerPath(betRef.id());
                        if (!rp.has(ppath)) {
                            rp.add(ppath);
                            playerPaths.push(ppath);
                        }
                    }
                });
                ctx.log('Given', this.playerBetColl.debugString(), updates.size(), 'playerBet updates', playerPaths.length, 'are newly ready', this.instanceId);
                retryCtl.resetTimeout();
                cb(ctx, this.readyPlayers, playerPaths); // must be last, may unsub
            }, err => {
                const retryableClass = bitiotic_1.FirebaseV9.shouldRetry(err);
                if (retryableClass !== undefined) {
                    ctx.log('Retrying after', retryableClass);
                    retryCtl.forceRetry();
                }
                else {
                    retryCtl.cancelTimeout();
                    errCb(ctx.chainError(err, 'PlayerBet onSnapshot for readyToGuess'));
                }
            }));
        }, () => {
            unsub();
            errCb(ctx.newError('Exhausted retries'));
        }, this.retryOpts(ctx));
        return unsub;
    }
    listenForPlayerGuessPresence(ctx, playerPath, cb, errCb) {
        const unsub = bitiotic_1.CancelTracker.track(`${this.getRoundPath(ctx)}.playerInput.${playerPath}.<guess>`, this.playerInputColl
            .makeDocRef(playerPath)
            .onSnapshot(docSnap => {
            if (docSnap && docSnap.docExists()) {
                const state = docSnap.data();
                if (state) {
                    const guessStr = state.guess;
                    if ((guessStr !== undefined) && (guessStr !== '')) {
                        if (this.playerGuessPresence === null) {
                            this.playerGuessPresence = new Set();
                        }
                        this.playerGuessPresence.add(playerPath);
                        cb(ctx, true);
                    }
                }
            }
        }, err => {
            errCb(ctx.chainError(err, `Listen for ${playerPath} playerInput`));
        }));
        return unsub;
    }
    // Listen for player guesses that are not empty.  Caller must invoke cancel callback
    // once they've seen all the players.
    listenForGuessPresence(ctx, currentPlayerPath, cb, errCb) {
        ctx = ctx.newCtx('listenForGuessPresence');
        const unsub = bitiotic_1.Retry.retryable(retryCtl => {
            return bitiotic_1.CancelTracker.track(`${this.getRoundPath(ctx)}.playerInput.guess`, this.playerInputColl
                .onSnapshot(updates => {
                if (this.playerGuessPresence === null) {
                    this.playerGuessPresence = new Set();
                }
                const gp = this.playerGuessPresence;
                const newPlayers = [];
                let notReadyCt = 0;
                let myGuess = null;
                updates.forEach(inputRef => {
                    var _a;
                    const guessStr = (_a = inputRef.data()) === null || _a === void 0 ? void 0 : _a.guess;
                    if (guessStr === undefined || guessStr === '') {
                        notReadyCt++;
                    }
                    else {
                        const pth = Persistent.asPlayerPath(inputRef.id());
                        if (!gp.has(pth)) {
                            gp.add(pth);
                            newPlayers.push(pth);
                        }
                        if (pth === currentPlayerPath) {
                            myGuess = guessStr;
                        }
                    }
                });
                retryCtl.resetTimeout();
                ctx.log('From', this.playerInputColl.debugString(), updates.size(), 'playerInput updates:', newPlayers.length, 'have guesses', notReadyCt, 'do not have guesses yet', this.instanceId);
                cb(ctx, this.playerGuessPresence, newPlayers, myGuess);
            }, (err) => {
                const retryableClass = bitiotic_1.FirebaseV9.shouldRetry(err);
                if (retryableClass !== undefined) {
                    ctx.log('Retrying after', retryableClass);
                    retryCtl.forceRetry();
                }
                else {
                    retryCtl.cancelTimeout();
                    errCb(ctx.chainError(err, 'PlayerInput onSnapshot'));
                }
            }));
        }, () => {
            // gave up
            ctx.log('Exhausted retries.');
            unsub();
            errCb(ctx.newError('Exhausted retries'));
        }, this.retryOpts(ctx));
        return unsub;
    }
    // Invoke listen callback exactly once, when guessLayout is defined
    listenForGuessLayout(ctx, cb, errCb) {
        ctx = ctx.newCtx('listenForGuessLayout');
        const gl = this.guessLayout;
        if (gl !== null) {
            ctx.log('guess layout already cached on Round');
            setTimeout(() => cb(ctx, gl), 0);
            return () => { };
        }
        const layoutDocRef = Round.getRoundLayoutDocRef(ctx, this.roundDoc);
        const unsub = bitiotic_1.Retry.retryable(retryCtl => {
            return bitiotic_1.CancelTracker.track(`${this.getRoundPath(ctx)}.guessLayout`, layoutDocRef
                .onSnapshot(layoutDoc => {
                const blob = layoutDoc.data();
                if (blob) {
                    ctx.assertEqual(layoutDoc.id(), 'singleton');
                    if (this.guessLayout === null) {
                        ctx.log('Got updated guess layout.');
                        this.guessLayout = blob;
                    }
                    retryCtl.cancelTimeout();
                    unsub();
                    cb(ctx, this.guessLayout);
                }
                else {
                    retryCtl.resetTimeout();
                }
            }, (err) => {
                const retryableClass = bitiotic_1.FirebaseV9.shouldRetry(err);
                if (retryableClass !== undefined) {
                    ctx.log('Retrying after', retryableClass);
                    retryCtl.forceRetry();
                }
                else {
                    retryCtl.cancelTimeout();
                    errCb(ctx.chainError(err, 'waiting on GuessLayout'));
                }
            }));
        }, () => {
            // gave up
            ctx.log('Exhausted retries.');
            unsub();
            errCb(ctx.newError('Exhausted retries'));
        }, this.retryOpts(ctx));
        return unsub;
    }
    getCachedBetMap(_ctx) {
        return this.playerBetMap;
    }
    currentBetMap(ctx) {
        const playerBets = [];
        this.getCachedPlayerPaths(ctx).forEach(playerPath => {
            const p = this.fetchPlayerBets(ctx, playerPath).then(plyrBet => {
                let bets = {};
                if (plyrBet !== null) {
                    bets = plyrBet.bets;
                }
                return { [playerPath]: bets };
            });
            playerBets.push(p);
        });
        return Promise.all(playerBets).then(bets => {
            const newMap = Object.assign({}, ...bets);
            this.playerBetMap = newMap; // Replace cached betmap
            return newMap;
        });
    }
    /**
     * Every player is granted $N $betUnit to bet each Round.
     * Plus players may bet previous winnings.
     */
    betStatusForPlayer(ctx, playerPath) {
        const status = {
            grantAvailable: this.betGrantCount * this.betUnit,
            grantBet: 0,
            winningsBet: 0,
        };
        if (this.playerBetMap !== null) {
            const bets = this.playerBetMap[playerPath]; // XXX cache okay to be stale?
            this.getBetSpotPaths(ctx).forEach(betSpotPath => {
                if (bets && bets[betSpotPath]) {
                    const val = bets[betSpotPath];
                    ctx.assertGT(val, 0);
                    if (status.grantAvailable === 0) {
                        status.winningsBet += val;
                    }
                    else if (val <= status.grantAvailable) {
                        status.grantAvailable -= val;
                        status.grantBet += val;
                    }
                    else { // partial
                        const rem = status.grantAvailable;
                        status.grantAvailable = 0;
                        status.grantBet += rem;
                        ctx.assertGT(val, rem);
                        status.winningsBet += (val - rem);
                    }
                }
            });
        }
        return status;
    }
    isBetMapComplete(ctx) {
        const playerCt = this.getNumPlayers(ctx);
        const betMap = this.playerBetMap;
        if (betMap === null) {
            return false;
        }
        const players = Object.keys(betMap);
        const keyCt = players.length;
        ctx.log(`Bet map (${keyCt}/${playerCt})`, betMap);
        if (keyCt >= playerCt) {
            const missing = [];
            ctx.log('Checking all players betMap status ...');
            // Each player should have bet at least their whole grant
            players.forEach(playerPathStr => {
                const playerPath = Persistent.asPlayerPath(playerPathStr);
                const playerStatus = this.betStatusForPlayer(ctx, playerPath);
                if (playerStatus.grantAvailable > 0) {
                    missing.push(playerPath);
                }
            });
            if (missing.length > 0) {
                ctx.log('Missing bets by:', missing);
                return false;
            }
            else {
                return true;
            }
        }
        return false;
    }
    // Invokes callback for each update, caller is required to unsubscribe when
    // "all" bets are received.
    listenForPlayerBets(ctx, cb, errCb) {
        const addToBetMap = (betMap, betRef, updated) => {
            const playerPath = Persistent.asPlayerPath(betRef.id());
            const playerBet = betRef.data();
            if (playerBet && playerBet.bets) {
                const newKeyCt = Object.keys(playerBet.bets).length; // num of betting chips set
                if (newKeyCt > 0) {
                    betMap[playerPath] = {
                        ...playerBet.bets,
                    };
                    updated.push(playerPath);
                }
                else {
                    // XXX allowed to clear a bet?  Or only replace with new bet?
                    const oldKeyCt = Object.keys(betMap[playerPath] || []).length;
                    if (oldKeyCt > 0) {
                        delete betMap[playerPath];
                        updated.push(playerPath);
                    }
                }
            }
        };
        ctx = ctx.newCtx('listenForPlayerBets');
        const unsub = bitiotic_1.Retry.retryable(retryCtl => {
            return bitiotic_1.CancelTracker.track(`${this.getRoundPath(ctx)}.playerBet.bets`, this.playerBetColl
                .onSnapshot(updates => {
                const updated = [];
                // Always set playerBetMap (even if all bets are unset)
                if (this.playerBetMap === null) {
                    this.playerBetMap = {};
                }
                const pbm = this.playerBetMap;
                updates.forEach(betRef => {
                    addToBetMap(pbm, betRef, updated);
                });
                retryCtl.resetTimeout();
                cb(ctx, pbm, updated); // cb invocation must be last, may unsub
            }, (err) => {
                const retryableClass = bitiotic_1.FirebaseV9.shouldRetry(err);
                if (retryableClass !== undefined) {
                    ctx.log('Retrying after', retryableClass);
                    retryCtl.forceRetry();
                }
                else {
                    retryCtl.cancelTimeout();
                    errCb(ctx.chainError(err, 'PlayerBet onSnapshot'));
                }
            }));
        }, () => {
            // gave up
            ctx.log('Exhausted retries.');
            unsub();
            errCb(ctx.newError('Exhausted retries'));
        }, this.retryOpts(ctx));
        return unsub;
    }
    listenForRoundResults(ctx, cb, errCb) {
        ctx = ctx.newCtx('listenForRoundResults');
        const rr = this.roundResults;
        if (rr !== null) {
            ctx.log('round results already cached on Round');
            setTimeout(() => cb(ctx, rr), 0);
            return () => { };
        }
        const roundResDocRef = Round.getRoundResultsDocRef(ctx, this.roundDoc);
        const unsub = bitiotic_1.Retry.retryable(retryCtl => {
            return bitiotic_1.CancelTracker.track(`${this.getRoundPath(ctx)}.results`, roundResDocRef.onSnapshot(resultsDoc => {
                const blob = resultsDoc.data();
                if (blob) {
                    ctx.log('Given results:', blob);
                    ctx.assertEqual(resultsDoc.id(), 'singleton');
                    if (this.roundResults === null) {
                        this.roundResults = blob;
                    }
                    retryCtl.cancelTimeout();
                    unsub();
                    cb(ctx, this.roundResults);
                }
                else {
                    ctx.log('doc at', roundResDocRef.debugString(), 'still undefined.');
                    retryCtl.resetTimeout();
                }
            }, (err) => {
                const retryableClass = bitiotic_1.FirebaseV9.shouldRetry(err);
                if (retryableClass !== undefined) {
                    ctx.log('Retrying after', retryableClass);
                    retryCtl.forceRetry();
                }
                else {
                    retryCtl.cancelTimeout();
                    errCb(ctx.chainError(err, 'waiting on RoundResults for', this.getRoundPath(ctx), this.getRoundNumber()));
                }
            }));
        }, () => {
            // gave up
            ctx.log('Exhausted retries.');
            unsub();
            errCb(ctx.newError('Exhausted retries'));
        }, this.retryOpts(ctx));
        return unsub;
    }
    getCachedResults(_ctx) {
        return this.roundResults;
    }
    isFinished(_ctx) {
        return this.roundResults !== null;
    }
    getResults(ctx) {
        if (this.roundResults === null) {
            const roundResDocRef = Round.getRoundResultsDocRef(ctx, this.roundDoc);
            ctx.log('Results not cached yet, fetching ...');
            return roundResDocRef.get().then(rDoc => {
                const r = rDoc.data();
                if (r) {
                    ctx.assertNotNull(r.answer);
                    if (this.roundResults === null) {
                        this.roundResults = r;
                    }
                    return this.roundResults;
                }
                return null;
            });
        }
        return Promise.resolve(this.roundResults);
    }
    getBankResults(ctx) {
        return this.playerBankColl.getDocs()
            .then(bankSnap => {
            const bankMap = {};
            bankSnap.forEach(doc => {
                const bank = doc.data();
                const playerPath = doc.id();
                if (bank) {
                    bankMap[playerPath] = bank;
                }
                else {
                    ctx.log('Warning: Player', playerPath, 'has no bank');
                }
            });
            return bankMap;
        });
    }
}
exports.Round = Round;
Round.NO_ROUND_NUMBER = -1;
function fillBetSpots(ctx, answerSpots) {
    const betSpots = {};
    ctx.assertEqual((answerSpots % 2), 1, `${answerSpots} must be odd`);
    const uf = Persistent.BetSpot_Underflow;
    betSpots[uf] = { betSpotPath: uf, payoutMultiplier: 6.0 };
    // betSpots["lowHalf"] = { betSpotPath: "lowHalf", payoutMultiplier: 1.0  };
    // betSpots["highHalf"] = { betSpotPath: "highHalf", payoutMultiplier: 1.0  };
    for (let i = 0; i < answerSpots; i++) {
        const betSpotPath = Persistent.makeBetSpotPath(i);
        const payoutMultiplier = Math.abs(i - Math.floor(answerSpots / 2)) + 2;
        betSpots[betSpotPath] = { betSpotPath, payoutMultiplier };
    }
    return betSpots;
}
//# sourceMappingURL=Round.js.map